import { MouseEvent } from "react";
import { TableCell } from "@mui/material";
import {
  GetCasesCountDocument,
  GetInitiativesDocument,
  Initiative,
  InitiativeStatus,
} from "~/operations";
import { isSpaceOrWorkspaceScope, useScope } from "~/hooks/useScope";
import { useDialogs } from "@toolpad/core";
import { useSecurityPipelineIntegrations } from "~/pages/integrations/security-pipeline/hooks/useSecurityPipelineIntegrations";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { CreateSecurityPipelinePrDialog } from "~/pages/integrations/security-pipeline/components/CreatePullRequest/CreateSecurityPipelinePRDialog";
import { InitiativeStatusChip } from "./InitiativeStatusChip";
import {
  TakeActionButton,
  TakeActionButtonProps,
} from "~/components/TakeAction/TakeActionButton";
import { useTickets } from "~/pages/tickets/hooks/useTickets";
import { CreateTicketDialog } from "~/pages/tickets/components/CreateTicketDialog";
import { useTicketingIntegrations } from "~/pages/tickets/hooks/useTicketingIntegrations";

interface InitiativesTableActionCellProps {
  initiative: Initiative;
}

export const InitiativesTableActionCell = ({
  initiative,
}: InitiativesTableActionCellProps) => {
  const { isFeatureEnabled } = useFeatureFlags();
  const { spaceScope, activeScope } = useScope();
  const dialogs = useDialogs();
  const { hasSecurityPipelineIntegrations } = useSecurityPipelineIntegrations();
  const { hasTicketingIntegration } = useTicketingIntegrations();

  const handleSecurityPipelinePRDialogOpen = () => {
    if (!spaceScope) return;

    const initiativeNode: Array<Initiative> = [initiative];

    return dialogs.open(CreateSecurityPipelinePrDialog, {
      initiatives: initiativeNode,
      spaceScope: spaceScope,
    });
  };

  const isSecurityPipelineEnabled =
    isFeatureEnabled("Security Pipeline") &&
    hasSecurityPipelineIntegrations &&
    initiative?.status === InitiativeStatus.NotStarted;

  const {
    handleCreateTicketingIntegrationModalOpen,
    loading: createCaseLoading,
    handleCreateCase,
    hasCreateTicketPermissions,
  } = useTickets({
    createCaseRefetchQueries: [GetCasesCountDocument],
  });

  const handleCreateTicketDialogOpen = () => {
    if (!spaceScope || !activeScope || !isSpaceOrWorkspaceScope(activeScope))
      return;

    dialogs.open(CreateTicketDialog, {
      isCombinedTicket: true,
      target: "asset",
      loading: createCaseLoading,
      onSave: (data) =>
        handleCreateCase({
          data,
          refetchQueries: [GetInitiativesDocument],
        }),
      selectedEntities: [
        {
          mrn: initiative.mrn,
          scopeMrn: activeScope?.mrn,
        },
      ],
      space: spaceScope,
      scope: activeScope,
      hasCreateCasePermissions: hasCreateTicketPermissions,
      isInitiative: true,
    });
  };

  const isTicketDisabled =
    !spaceScope ||
    !activeScope ||
    !isSpaceOrWorkspaceScope(activeScope) ||
    !hasCreateTicketPermissions;

  const availableRemediations: TakeActionButtonProps["availableRemediations"] =
    {
      ticket: {
        active: true,
        onClick: hasTicketingIntegration
          ? handleCreateTicketDialogOpen
          : handleCreateTicketingIntegrationModalOpen,
        disabled: isTicketDisabled,
      },
      exception: {
        active: false,
      },
      securityPipeline: {
        active: isSecurityPipelineEnabled,
        onClick: handleSecurityPipelinePRDialogOpen,
      },
    };
  return (
    <TableCell
      align="left"
      onClick={(e: MouseEvent<HTMLTableCellElement>) => e.stopPropagation()}
      sx={{ position: "relative", zIndex: 1 }}
    >
      {initiative.status === InitiativeStatus.NotStarted ? (
        <TakeActionButton
          availableRemediations={availableRemediations}
          type="action-cell"
        />
      ) : (
        <InitiativeStatusChip status={initiative.status} />
      )}
    </TableCell>
  );
};
